// ============================================================================
// AUTH GUARD UTILITIES
// ============================================================================
// Centralised helpers for the three session-lifecycle behaviours that every
// app scaffolded from react-project-template inherits:
//
//   1. COMPLETE LOGOUT — wipe every piece of client-side session state, notify
//      sibling tabs, and replace the history entry so browser Back cannot
//      return to a cached authenticated page.
//   2. CROSS-TAB AUTH SYNC — login in one tab hops sibling login screens into
//      the app; logout in one tab signs every other tab out.
//   3. SINGLE-ACTIVE-TAB PRESENCE — the shell runs an internal (MDI-style) tab
//      system inside ONE browser tab, so a second browser tab is blocked with
//      a "Use here" takeover screen (see `TabTakeoverGuard`).
//
// All three are origin-scoped (localStorage + BroadcastChannel), which is
// exactly the scope we want: one browser profile == one session.
//
// Ported from react-project-template's src/utils/authGuard.ts, including the
// 2026-08-12 BFCache fix (a `pagehide` with `persisted === true` is a mobile
// freeze, NOT a departure) — do not re-port an older copy over this one.

import { tokenManager } from '../auth/tokenManager';

// ----------------------------------------------------------------------------
// Channel / key names
// ----------------------------------------------------------------------------
// localStorage and BroadcastChannel are scoped per ORIGIN, not per path. If two
// apps built from the template are ever served from the SAME origin under
// different paths, each must use its own `APP_SCOPE` so their auth channels
// don't cross-talk (a logout in app A would otherwise sign the user out of
// app B). Fairbooks uses `fbp` (the template default is `app`).
const APP_SCOPE = 'fbp';

const LOGOUT_STORAGE_KEY = `__${APP_SCOPE}_logout_event`;
const LOGIN_STORAGE_KEY = `__${APP_SCOPE}_login_event`;
const AUTH_CHANNEL_NAME = `${APP_SCOPE}-auth-channel`;
const TAB_HEARTBEAT_KEY = `__${APP_SCOPE}_tab_heartbeat`;

const HEARTBEAT_INTERVAL_MS = 2000;
const HEARTBEAT_FRESH_MS = 5000;

/**
 * localStorage keys that survive a logout. These hold per-browser UI
 * personalisation (dark mode, tab mode, sidebar auto-collapse, footer
 * visibility) — NOT session state — so wiping them on every logout would be a
 * pointless UX regression. Everything else is removed.
 *
 * Keep in sync with `preferencesService.ts` (`APP_USER_PREFERENCES`).
 */
const PRESERVED_STORAGE_KEYS: readonly string[] = ['APP_USER_PREFERENCES'];

let broadcastChannel: BroadcastChannel | null = null;

const getBroadcastChannel = (): BroadcastChannel | null => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  }
  return broadcastChannel;
};

// ============================================================================
// TEARDOWN FLAG
// ============================================================================

let sessionTeardownStarted = false;

/**
 * TRUE once ANY teardown path has begun — explicit logout, a logout broadcast
 * from a sibling tab, or a session-expiry redirect from the axios interceptor.
 *
 * `beforeunload` handlers MUST consult this before prompting about unsaved
 * work: the session is already gone by then, so nothing can be saved, and a
 * native "Leave site?" dialog would only strand the user in a dead shell.
 * The shell's own logout button has its own in-component flag; this covers the
 * two paths that originate OUTSIDE the shell.
 */
export const isSessionTeardownStarted = (): boolean => sessionTeardownStarted;

// ============================================================================
// STORAGE CLEANUP
// ============================================================================

/**
 * Wipe every session key from localStorage (except `PRESERVED_STORAGE_KEYS`)
 * and all of sessionStorage, then drop the in-memory access token.
 *
 * Deliberately NOT a targeted `removeItem` list: any partial cleanup risks
 * leaving stale user context (permissions, scope selections, cached navigation,
 * tenant branding) that the app would then read on the next login. Auth is
 * fail-closed — an unknown leftover key is a bug waiting to happen.
 */
export const clearAllAuthStorage = (): void => {
  // Single choke point for every teardown path, which makes it the right place
  // to raise the flag — see `isSessionTeardownStarted`.
  sessionTeardownStarted = true;

  try {
    const preserved = PRESERVED_STORAGE_KEYS.map(
      (key) => [key, localStorage.getItem(key)] as const,
    );
    localStorage.clear();
    preserved.forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });
  } catch {
    /* storage may be unavailable in some contexts (private mode quotas, etc.) */
  }
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
  try {
    // tokenManager caches the access token in a module-scoped variable so it
    // isn't read from localStorage on every request — clearing storage alone
    // would leave that copy live until the next full page load.
    tokenManager.clearAccessToken();
  } catch {
    /* ignore */
  }
};

// ============================================================================
// CROSS-TAB LOGOUT
// ============================================================================

/**
 * Notify every other tab of this app that a logout has occurred.
 * Uses BroadcastChannel where available, with a `storage` event fallback so
 * older browsers (and cross-origin iframes) still receive the signal.
 */
export const broadcastLogout = (): void => {
  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage({ type: 'logout', ts: Date.now() });
    } catch {
      /* ignore */
    }
  }
  try {
    // Fire a storage event by writing + removing a sentinel key. Other tabs
    // receive `window` storage events (they don't fire in the originating tab,
    // which is exactly what we want).
    localStorage.setItem(LOGOUT_STORAGE_KEY, String(Date.now()));
    localStorage.removeItem(LOGOUT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Subscribe to logout broadcasts from other tabs.
 * Returns an unsubscribe function.
 */
export const initCrossTabLogout = (onLogout: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === LOGOUT_STORAGE_KEY && event.newValue) {
      onLogout();
    }
  };
  window.addEventListener('storage', handleStorage);

  const channel = getBroadcastChannel();
  const handleMessage = (event: MessageEvent) => {
    if (event?.data?.type === 'logout') {
      onLogout();
    }
  };
  channel?.addEventListener('message', handleMessage);

  return () => {
    window.removeEventListener('storage', handleStorage);
    channel?.removeEventListener('message', handleMessage);
  };
};

// ============================================================================
// CROSS-TAB LOGIN
// ============================================================================

/**
 * Notify every other tab of this app that a login has occurred.
 * Mirror of `broadcastLogout` — same dual-channel (BroadcastChannel + storage
 * event) strategy so sibling tabs sitting on the login screen can hop straight
 * into the app without waiting for a manual refresh.
 */
export const broadcastLogin = (): void => {
  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage({ type: 'login', ts: Date.now() });
    } catch {
      /* ignore */
    }
  }
  try {
    localStorage.setItem(LOGIN_STORAGE_KEY, String(Date.now()));
    localStorage.removeItem(LOGIN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Subscribe to login broadcasts from other tabs.
 * Returns an unsubscribe function.
 */
export const initCrossTabLogin = (onLogin: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === LOGIN_STORAGE_KEY && event.newValue) {
      onLogin();
    }
  };
  window.addEventListener('storage', handleStorage);

  const channel = getBroadcastChannel();
  const handleMessage = (event: MessageEvent) => {
    if (event?.data?.type === 'login') {
      onLogin();
    }
  };
  channel?.addEventListener('message', handleMessage);

  return () => {
    window.removeEventListener('storage', handleStorage);
    channel?.removeEventListener('message', handleMessage);
  };
};

// ============================================================================
// SINGLE-TAB PRESENCE PROTOCOL
// ============================================================================
// Enforces "one active tab per browser profile" for authenticated sessions.
// Every tab does two things:
//   1. Writes a heartbeat to localStorage every HEARTBEAT_INTERVAL_MS. A tab is
//      considered "alive" if its heartbeat is fresher than HEARTBEAT_FRESH_MS.
//   2. Talks over BroadcastChannel: pings on mount, pongs on ping, announces
//      departure on unload, and can broadcast a takeover when the user chooses
//      to "use here" — which locks out every other tab.
// The heartbeat is the fallback for browsers without BroadcastChannel: ping /
// pong only helps when both tabs are running simultaneously, whereas the
// heartbeat also catches the case where the other tab was there first.

const generateTabId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to fallback */
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

interface HeartbeatRecord {
  tabId: string;
  ts: number;
}

const readHeartbeat = (): HeartbeatRecord | null => {
  try {
    const raw = localStorage.getItem(TAB_HEARTBEAT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as HeartbeatRecord;
  } catch {
    return null;
  }
};

const writeHeartbeat = (tabId: string): void => {
  try {
    localStorage.setItem(TAB_HEARTBEAT_KEY, JSON.stringify({ tabId, ts: Date.now() }));
  } catch {
    /* ignore */
  }
};

const clearOwnHeartbeat = (tabId: string): void => {
  const current = readHeartbeat();
  if (current?.tabId === tabId) {
    try {
      localStorage.removeItem(TAB_HEARTBEAT_KEY);
    } catch {
      /* ignore */
    }
  }
};

const isForeignHeartbeatFresh = (myTabId: string): boolean => {
  const current = readHeartbeat();
  if (!current || current.tabId === myTabId) return false;
  return Date.now() - current.ts < HEARTBEAT_FRESH_MS;
};

interface PresenceHandlers {
  /** Called if another tab of this app is already alive when we mount. */
  onExistingTabDetected: () => void;
  /** Called if another tab takes over — this tab must lock itself out. */
  onTakeoverReceived: () => void;
  /** Called when another tab announces it is closing/unloading, so a blocked
   *  tab can promote itself to active instead of being stranded. */
  onOtherTabLeft?: () => void;
}

/**
 * Start the single-tab presence protocol for this tab.
 * Returns a stable `tabId`, a `takeOver()` function that locks out every other
 * tab, and a `stop()` teardown function.
 */
export const initTabPresence = (
  handlers: PresenceHandlers,
): { tabId: string; takeOver: () => void; stop: () => void } => {
  const tabId = generateTabId();
  const channel = getBroadcastChannel();

  // Step 1 — the heartbeat check is ONLY a fallback for browsers without
  // BroadcastChannel. When BroadcastChannel is available we rely purely on the
  // live ping/pong below: a tab that simply reloaded (e.g. the post-login or
  // post-logout redirect) must NOT falsely detect its own just-written
  // heartbeat from the previous page load and flash the blocked screen. A
  // genuinely-live sibling tab always answers the ping with a pong.
  if (!channel && isForeignHeartbeatFresh(tabId)) {
    handlers.onExistingTabDetected();
  }

  // Step 2 — broadcast a ping so any live tab responds with a pong.
  const handleMessage = (event: MessageEvent) => {
    const data = event?.data;
    if (!data || typeof data !== 'object' || data.tabId === tabId) return;

    if (data.type === 'presence-ping') {
      channel?.postMessage({ type: 'presence-pong', tabId });
    } else if (data.type === 'presence-pong') {
      handlers.onExistingTabDetected();
    } else if (data.type === 'presence-takeover') {
      handlers.onTakeoverReceived();
    } else if (data.type === 'presence-leaving') {
      handlers.onOtherTabLeft?.();
    }
  };
  channel?.addEventListener('message', handleMessage);

  try {
    channel?.postMessage({ type: 'presence-ping', tabId });
  } catch {
    /* ignore */
  }

  // Step 3 — start heartbeat.
  writeHeartbeat(tabId);
  const interval = window.setInterval(() => writeHeartbeat(tabId), HEARTBEAT_INTERVAL_MS);

  // Step 4 — on unload, announce departure (so a blocked sibling can promote
  // itself) and clear our heartbeat so a reload doesn't self-lock.
  const handleUnload = (event?: Event) => {
    // `pagehide` is NOT only "the page is going away". Mobile browsers fire it
    // when the page is FROZEN into the BFCache — the user takes a call, answers
    // a message, switches app — and `event.persisted === true` says exactly
    // that: still alive, to be restored via `pageshow`.
    //
    // Treating that as departure is how a phone user loses the tab they never
    // left: we broadcast `presence-leaving` (a blocked sibling promotes itself)
    // and clear our own heartbeat, so returning from a 30-second call lands on
    // the blocked screen.
    if ((event as PageTransitionEvent | undefined)?.persisted) return;

    try {
      channel?.postMessage({ type: 'presence-leaving', tabId });
    } catch {
      /* ignore — best-effort during unload */
    }
    clearOwnHeartbeat(tabId);
  };

  // Resume-time re-handshake. While frozen our timers are paused, so the
  // heartbeat goes stale and another tab may LEGITIMATELY have taken over in the
  // meantime. Re-assert presence, then ping: a live sibling answers with a pong
  // and we become the blocked tab — the same contract as a fresh load, rather
  // than two tabs both believing they are active.
  const handlePageShow = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    writeHeartbeat(tabId);
    try {
      channel?.postMessage({ type: 'presence-ping', tabId });
    } catch {
      /* ignore */
    }
  };

  window.addEventListener('pagehide', handleUnload);
  window.addEventListener('beforeunload', handleUnload);
  window.addEventListener('pageshow', handlePageShow);

  const takeOver = () => {
    try {
      channel?.postMessage({ type: 'presence-takeover', tabId });
    } catch {
      /* ignore */
    }
    // Reclaim the heartbeat immediately so the other tab's next read sees ours.
    writeHeartbeat(tabId);
  };

  const stop = () => {
    channel?.removeEventListener('message', handleMessage);
    window.removeEventListener('pagehide', handleUnload);
    window.removeEventListener('beforeunload', handleUnload);
    window.removeEventListener('pageshow', handlePageShow);
    window.clearInterval(interval);
    clearOwnHeartbeat(tabId);
  };

  return { tabId, takeOver, stop };
};

// ============================================================================
// BACK-NAVIGATION GUARD (login view)
// ============================================================================

/**
 * Trap the browser Back button on the current URL. Every time the user tries
 * to pop back into an authenticated page we re-push the current entry, so the
 * URL stays put. Also guards against the Back/Forward Cache restoring a stale
 * authenticated page — if that happens we force a full reload.
 *
 * Intended to be installed from the LOGIN view (the post-logout landing).
 */
export const installBackNavigationGuard = (): (() => void) => {
  const push = () => {
    try {
      window.history.pushState(null, '', window.location.href);
    } catch {
      /* ignore */
    }
  };

  push();

  const onPopState = () => push();
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      // Page restored from BFCache while unauthenticated — hard reload so any
      // cached rendered UI is discarded.
      window.location.reload();
    }
  };

  window.addEventListener('popstate', onPopState);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('pageshow', onPageShow);
  };
};

// ============================================================================
// LOGOUT
// ============================================================================

/**
 * Perform a complete logout:
 *   1. Best-effort server call (revoke refresh token) — errors are swallowed
 *      so the client-side cleanup always runs
 *   2. Broadcast to sibling tabs
 *   3. Clear all client storage
 *   4. `location.replace('/')` so the current history entry is OVERWRITTEN and
 *      the login view renders (Back can't return to the app)
 *
 * `serverLogout` is optional so callers that have already revoked the session
 * (or run in legacy/mock auth mode) can skip the network round-trip.
 *
 * `redirectDelayMs` holds the redirect back briefly so a "logged out" toast is
 * readable before the document navigates away. The broadcast and the storage
 * wipe are NOT delayed — the session is dead the moment this is called.
 */
export const performLogout = async (
  serverLogout?: () => Promise<unknown> | void,
  options?: { redirectDelayMs?: number },
): Promise<void> => {
  if (serverLogout) {
    try {
      await serverLogout();
    } catch {
      /* backend call is best-effort — proceed with local cleanup regardless */
    }
  }

  broadcastLogout();
  clearAllAuthStorage();

  const delay = options?.redirectDelayMs ?? 0;
  if (delay > 0) {
    window.setTimeout(() => window.location.replace('/'), delay);
    return;
  }
  window.location.replace('/');
};
